// api/auth.service.js
import API from './axiosConfig';

/**
 * Helper function to check if JWT token is expired
 * @param {string} token - JWT token
 * @returns {boolean} - True if token is expired or invalid
 */
export const isTokenExpired = (token) => {
  if (!token) return true;
  
  try {
    // Decode JWT token payload
    const payload = JSON.parse(atob(token.split('.')[1]));
    const expiry = payload.exp * 1000; // Convert to milliseconds
    
    // Check if token is expired (current time > expiry)
    return Date.now() >= expiry;
  } catch (error) {
    // Invalid token format
    return true;
  }
};

/**
 * Get token expiration time in milliseconds
 * @param {string} token - JWT token
 * @returns {number|null} - Expiration timestamp or null if invalid
 */
export const getTokenExpiry = (token) => {
  if (!token) return null;
  
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000;
  } catch (error) {
    return null;
  }
};

/**
 * User login with email and password
 * @param {Object} credentials - User credentials
 * @param {string} credentials.email - User email
 * @param {string} credentials.password - User password
 * @returns {Promise} - Login response with token and user data
 */
export const login = async (credentials) => {
  try {
    const response = await API.post('/login', credentials);
    
    // Store refresh token if provided
    if (response.data.refresh_token) {
      localStorage.setItem('refresh_token', response.data.refresh_token);
    }
    
    // Store main access token
    if (response.data.token) {
      localStorage.setItem('token', response.data.token);
    }
    
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Verify temporary password for first-time login
 * @param {Object} data - Verification data
 * @param {string} data.email - User email
 * @param {string} data.temp_password - Temporary password
 * @returns {Promise} - Verification response
 */
export const verifyTempPassword = async (data) => {
  const response = await API.post('/verify-temp-password', data);
  return response.data;
};

/**
 * Set new password for user (first-time login or password reset)
 * @param {Object} data - Password data
 * @param {string} data.email - User email
 * @param {string} data.new_password - New password
 * @returns {Promise} - Password update response
 */
export const setNewPassword = async (data) => {
  const response = await API.post('/set-new-password', data);
  return response.data;
};

/**
 * Refresh authentication token using refresh token
 * @returns {Promise} - New token response
 */
export const refreshToken = async () => {
  try {
    const refresh_token = localStorage.getItem('refresh_token');
    
    if (!refresh_token) {
      throw new Error('No refresh token available');
    }
    
    const response = await API.post('/refresh-token', {
      refresh_token
    });
    
    // Store new token
    if (response.data.token) {
      localStorage.setItem('token', response.data.token);
    }
    
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Logout user and clear all session data
 */
export const logout = () => {
  // Clear all stored tokens and user data
  localStorage.removeItem('token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('user');
  localStorage.removeItem('nav_list');
  localStorage.removeItem('savedEmail');
  
  // Clear session storage
  sessionStorage.clear();
  
  // Redirect to login page
  window.location.href = '/login';
};

/**
 * Check if user is currently authenticated
 * @returns {boolean} - True if user has valid token
 */
export const isAuthenticated = () => {
  const token = localStorage.getItem('token');
  
  // Check if token exists and is not expired
  if (token && !isTokenExpired(token)) {
    return true;
  }
  
  return false;
};

/**
 * Get current authenticated user
 * @returns {Object|null} - User object or null if not authenticated
 */
export const getCurrentUser = () => {
  const userData = localStorage.getItem('user');
  
  if (userData && isAuthenticated()) {
    return JSON.parse(userData);
  }
  
  return null;
};

/**
 * Get current auth token
 * @returns {string|null} - Token or null if not available
 */
export const getToken = () => {
  return localStorage.getItem('token');
};

/**
 * Set up automatic token refresh timer
 * @param {Function} onTokenRefresh - Callback when token is refreshed
 * @returns {number} - Interval ID for cleanup
 */
export const setupTokenRefresh = (onTokenRefresh = null) => {
  // Check token expiry every 5 minutes
  const intervalId = setInterval(async () => {
    const token = getToken();
    
    if (token && !isTokenExpired(token)) {
      const expiryTime = getTokenExpiry(token);
      const currentTime = Date.now();
      const timeUntilExpiry = expiryTime - currentTime;
      
      // Refresh token if it will expire in less than 5 minutes
      if (timeUntilExpiry < 5 * 60 * 1000) {
        try {
          const response = await refreshToken();
          
          // Call callback if provided
          if (onTokenRefresh && response.token) {
            onTokenRefresh(response.token);
          }
        } catch (error) {
          console.error('Auto token refresh failed:', error);
        }
      }
    }
  }, 5 * 60 * 1000); // Check every 5 minutes
  
  return intervalId;
};

/**
 * Clear token refresh interval
 * @param {number} intervalId - Interval ID to clear
 */
export const clearTokenRefresh = (intervalId) => {
  if (intervalId) {
    clearInterval(intervalId);
  }
};